import { apiClient } from "./api/client";
import type {
  ProjectDocument,
  ProjectValidationReport,
  TerrainRefreshResult,
  TerrainTargetPreview,
  TopologyCommand,
  VerticalReference,
} from "../features/network";
import type { JobRequirement } from "../features/scenarios";

export interface ProjectSummary {
  id: string;
  name: string;
  read_only: boolean;
  revision: number;
  saved_optimization_count: number;
  saved_simulation_count: number;
  source: "editable" | "portable_import";
  updated_at: string;
}

export interface EditableProject extends ProjectDocument {
  candidate_pump_sites: NonNullable<ProjectDocument["candidate_pump_sites"]>;
  created_at: string;
  headloss_model: "darcy_weisbach" | "hazen_williams";
  safety_settings: {
    component_pressure_margin_fraction: number;
    driver_power_reserve_fraction: number;
    minimum_profile_pressure_gauge_pa: number | null;
    npsh_margin_m: number;
  };
  safety_settings_acknowledged_at: string | null;
  scenarios: Array<{
    fleet_settings: Array<{
      available: boolean;
      driver_power_derate_fraction: number;
      fleet_unit_id: string;
      maximum_engine_speed_rpm: number | null;
    }>;
    id: string;
    kind: "contingency" | "normal" | "peak";
    name: string;
    required: boolean;
    sinks: Array<{
      minimum_residual_pressure_gauge_pa: number | null;
      required_flow_m3_s: number | null;
      job_requirement?: JobRequirement | null;
      sink_node_id: string;
    }>;
    sources: Array<{
      hydraulic_head_m: number | null;
      maximum_withdrawal_flow_m3_s: number | null;
      source_node_id: string;
    }>;
    water: {
      density_kg_m3: number;
      kinematic_viscosity_m2_s: number;
      temperature_k: number;
      vapor_pressure_absolute_pa: number;
    };
    weight: number;
    [key: string]: unknown;
  }>;
  terrain_accuracy_warning_acknowledged_at: string | null;
  updated_at: string;
  [key: string]: unknown;
}

const SELECTED_PROJECT_KEY = "flow-wizard.selected-project-id";

export function selectedProjectId(): string | null {
  return window.localStorage.getItem(SELECTED_PROJECT_KEY);
}
export function selectProject(id: string): void {
  window.localStorage.setItem(SELECTED_PROJECT_KEY, id);
}

function topologyPayload(command: TopologyCommand): Record<string, unknown> {
  switch (command.type) {
    case "draw":
      return {
        type: command.type,
        geometry: command.geometry,
        name: command.name,
        ...("location" in command ? { location: command.location } : {}),
        ...("path" in command && command.path !== undefined
          ? { path: command.path }
          : {}),
        ...("startNodeId" in command
          ? {
              start_node_id: command.startNodeId,
              end_node_id: command.endNodeId,
              hose_type_id: command.hoseTypeId,
            }
          : {}),
        ...("maximumPhysicalUnits" in command
          ? {
              anchor_link_id: command.anchorLinkId ?? null,
              anchor_node_id: command.anchorNodeId ?? null,
              maximum_physical_units: command.maximumPhysicalUnits,
              allowed_arrangements: command.allowedArrangements,
            }
          : {}),
      };
    case "edit_node_role":
      return {
        type: command.type,
        node_id: command.nodeId,
        node_type: command.nodeType,
      };
    case "edit_link_path":
      return {
        type: command.type,
        link_id: command.linkId,
        path: command.path,
      };
    case "split_link":
      return {
        type: command.type,
        link_id: command.linkId,
        distance_from_start_m: command.distanceFromStartM,
      };
    case "join_links":
      return {
        type: command.type,
        first_link_id: command.firstLinkId,
        second_link_id: command.secondLinkId,
      };
    case "reverse_link":
      return { type: command.type, link_id: command.linkId };
    case "delete_element":
      return { type: command.type, element_id: command.elementId };
    case "snap_endpoint":
      return {
        type: command.type,
        link_id: command.linkId,
        endpoint: command.endpoint,
        target_node_id: command.targetNodeId,
        tolerance_m: command.toleranceM,
      };
  }
}

export const projectApi = {
  capture: (id: string) => apiClient.post(`/projects/${id}/portable-snapshot`),
  create: (name: string, verticalReference: VerticalReference) =>
    apiClient.post<EditableProject>("/projects", {
      json: { name, vertical_reference: verticalReference },
    }),
  get: (id: string) => apiClient.get<EditableProject>(`/projects/${id}`),
  getPortable: (id: string) =>
    apiClient.get<Record<string, unknown>>(`/projects/${id}/portable-snapshot`),
  list: () => apiClient.get<ProjectSummary[]>("/projects"),
  save: (project: EditableProject) =>
    apiClient.put<EditableProject>(
      `/projects/${project.id}?expected_revision=${project.revision}`,
      { json: project },
    ),
  validate: (id: string) =>
    apiClient.get<ProjectValidationReport>(`/projects/${id}/validation`),
  terrainTargets: (projectId: string, maximumSpacingM: number) =>
    apiClient.get<TerrainTargetPreview>(
      `/projects/${projectId}/terrain/targets?maximum_spacing_m=${encodeURIComponent(maximumSpacingM)}`,
    ),
  refreshUsgsTerrain: (
    project: Pick<EditableProject, "id" | "revision">,
    maximumSpacingM: number,
  ) =>
    apiClient.post<TerrainRefreshResult>(
      `/projects/${project.id}/terrain/usgs-refresh`,
      {
        json: {
          expected_revision: project.revision,
          maximum_spacing_m: maximumSpacingM,
        },
      },
    ),
  refreshOpenMeteoTerrain: (
    project: Pick<EditableProject, "id" | "revision">,
    maximumSpacingM: number,
  ) =>
    apiClient.post<TerrainRefreshResult>(
      `/projects/${project.id}/terrain/open-meteo-refresh`,
      {
        json: {
          expected_revision: project.revision,
          maximum_spacing_m: maximumSpacingM,
        },
      },
    ),
  refreshManualTerrain: (
    project: Pick<EditableProject, "id" | "revision">,
    values: {
      csvText: string;
      filename: string;
      maximumSpacingM: number;
      surveyed: boolean;
    },
  ) =>
    apiClient.post<TerrainRefreshResult>(
      `/projects/${project.id}/terrain/manual-refresh`,
      {
        json: {
          csv_text: values.csvText,
          expected_revision: project.revision,
          filename: values.filename,
          maximum_spacing_m: values.maximumSpacingM,
          surveyed: values.surveyed,
        },
      },
    ),
  topology: (
    project: Pick<EditableProject, "id" | "revision">,
    command: TopologyCommand,
  ) =>
    apiClient.post<EditableProject>(
      `/projects/${project.id}/topology-commands`,
      {
        json: {
          command: topologyPayload(command),
          expected_revision: project.revision,
        },
      },
    ),
};
